import { useRef, useState } from "react";

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Collapse, Grid, IconButton, Stack, Typography } from "@mui/material";
import Card2 from "components/elements/Card2";
import { Settings3 } from "components/icons";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import { useTranslation } from "react-i18next";
import { SubscriptionType } from "types/enums";
import { IStrategiesSummary } from "types/types";
import { hasAllValuesFalse, isAtTierOrHigher } from "utils";

import SummaryCharts from "./Charts";
import Item from "./Item";
import CashbackSatsItem from "./Item/CashbackSats";
import SkeletonItem from "./Item/Skeleton";

interface IProps {
  data?: IStrategiesSummary;
  onOpenModal: () => void;
}

const Summaries = ({ data, onOpenModal }: IProps) => {
  const { t } = useTranslation<string>();
  const { colors } = useMUIThemeModeContext();
  const { summariesSettings, subscriptionType, subscriptionPaused } = useUserDataContext();
  const isPremium = isAtTierOrHigher(subscriptionType ?? SubscriptionType.FREE, SubscriptionType.PREMIUM);
  const [chartsOpen, setChartsOpen] = useState(false);
  const chartsRef = useRef<HTMLDivElement>(null);

  const dcaCurrencyPairs = (data?.dcaStrategiesSummaries ?? []).map((s) => s.currencyPair);
  const hasDcaItems = dcaCurrencyPairs.length > 0;

  if (hasAllValuesFalse(summariesSettings) || !isPremium) return null;

  const getList = () => {
    const summaryList: React.ReactNode[] = [];

    data?.gridStrategiesSummaries.forEach((summary, index) =>
      summaryList.push(<Item summary={summary} key={`GridSummaryItem-${index}`} isGrid />)
    );
    data?.dcaStrategiesSummaries.forEach((summary, index) =>
      summaryList.push(<Item summary={summary} key={`DcaSummaryItem-${index}`} />)
    );

    return summaryList;
  };

  const handleChartsScroll = () => {
    chartsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Card2
      p={1.5}
      mb={4}
      height={"100%"}
      title={
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography fontSize={"1rem"} fontWeight={600}>
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              {t("summaries.title")}
            </Box>
            <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
              {t("summaries.titleShort")}
            </Box>
          </Typography>
          {hasDcaItems && !subscriptionPaused && (
            <Button
              color="info"
              variant="outlined"
              size="small"
              onClick={() => setChartsOpen((prev) => !prev)}
              endIcon={chartsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            >
              {t("summaries.charts.button")}
            </Button>
          )}
        </Stack>
      }
      action={
        <IconButton size="small" onClick={onOpenModal}>
          <Settings3 sx={{ fontSize: 18, color: colors.gray700 }} />
        </IconButton>
      }
      childrenBoxSx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
      }}
      headerStackSx={{
        mb: 2,
      }}>
      <Box width={"100%"}>
        <Grid container spacing={2}>
          {summariesSettings.cashbackSats && <CashbackSatsItem />}
          {data ? getList() : <SkeletonItem canRenderCashbackSats={summariesSettings.cashbackSats} />}
        </Grid>
        <Collapse in={chartsOpen} onEntered={handleChartsScroll} unmountOnExit>
          <Box ref={chartsRef} p={{ xs: 1, md: 2 }} mt={{ xs: 1, md: 2 }} bgcolor={colors.gray25} borderRadius={"10px"}>
            <SummaryCharts currencyPairs={dcaCurrencyPairs} />
            <Box mt={2} display="flex" justifyContent="center">
              <Button color="info" variant="outlined" size="small" onClick={() => setChartsOpen(false)} endIcon={<KeyboardArrowUpIcon />}>
                {t("summaries.charts.collapseButton")}
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Card2>
  );
};

export default Summaries;
